/**
 * Todo el texto visible de la página de precios, en inglés (tal cual la
 * maqueta «Xotski Pricing v4») y en español. Vive aquí y no en
 * `translations.ts` porque es copy ESTRUCTURADO (tablas, listas, tarjetas)
 * que sólo usa esta página; el objeto en español tiene el mismo tipo que el
 * inglés, así que faltar una cadena rompe el type-check. La página elige el
 * idioma con `usePrefsStore`, igual que `useT()`.
 */

/** Una viñeta de tarjeta: texto, o `{ soon }` para lo que aún no existe. */
export type Unlock = string | { soon: string };

export type CompareKind = 'price' | 'num' | 'text' | 'key';
export type CompareCell = string | number | boolean;
/** Una fila de la tabla à la carte. `soon` = no construido, sin precio. */
export interface AlcRow {
  item: string;
  price: string;
  unit: string;
  note: string;
  soon?: boolean;
}

export interface CompareRow {
  label: string;
  desc: string;
  cells: [CompareCell, CompareCell, CompareCell, CompareCell];
  kind: CompareKind;
  /** Fila aún no construida: se pinta apagada y con «Próximamente». */
  soon?: boolean;
}

export const PRICING_COPY_EN = {
  nav: { detail: 'All the detail', startFree: 'Start free' },
  hero: {
    title1: 'Four plans.',
    title2: 'One of them free',
    points: [
      { strong: 'Experiential tech first.', rest: 'Built for the field, not a spreadsheet.' },
      { strong: 'Premiums, organised.', rest: 'Inventory by brand, by project, by event.' },
      { strong: 'Games they actually play.', rest: 'Immersive by design, branded as yours.' },
      { strong: 'AI on the depletion curve.', rest: 'It reads the stock and tells you what to order next.' },
      { strong: 'Annual, not monthly.', rest: 'Events are seasonal and bursty. Pricing should match.' },
    ],
  },
  modes: { plans: 'The plans', calc: 'Size my year' },
  plans: {
    names: { free: 'Free', alc: 'À la carte', xk1: 'XK1', xk2: 'XK2', enterprise: 'Enterprise' },
    cards: {
      free: { blurb: 'Build the whole tour, run {freeEvents} events, your branding on.', cta: 'Start free' },
      xk1: { blurb: 'All {games} games and a live dashboard. {xk1Events} events a year.', cta: 'Choose XK1' },
      xk2: { blurb: 'Leads, live ops and reporting for a full season of {xk2Events} events.', cta: 'Choose XK2' },
      enterprise: { blurb: 'Stops metering events and starts metering seats. SSO, SLA, a named CSM.', cta: 'Talk to us' },
    },
    mostPopular: 'Most popular',
    yourMatch: 'Your match',
    freePer: 'Free for as long as you stay inside it',
    perMonth: '{amount} a month, billed annually',
    perFrom: 'From {amount} a month, billed annually',
    from: 'From ',
    freeWall: 'Free stops at {freeEvents} events. For more, upgrade to XK1 or go à la carte.',
    seal: 'Powered by Onxite on every game.',
    soon: 'Coming soon',
    notSure: 'Not sure which one? Size your year and the price falls out of it.',
    openCalc: 'Open the calculator',
  },
  unlocks: {
    free: ['{freeEvents} events a year, real prizes, data on', 'Your logo and colours, with the Onxite seal', 'Demographics: who actually engaged', '{freeGames}', '{freeBrands} brand, {freeProjects} project, {freeX} xotskies an event'] as Unlock[],
    alc: ['One editor seat at {seat} a year, then {event} an event', 'Your branding on every event', 'Demographics on every event', 'Real prizes and inventory tracking'] as Unlock[],
    xk1: ['All {games} games. Real-time dashboard, smart inventory, push alerts', '{xk1Events} events, {xk1Projects} projects, {xk1Brands} brands', '{xk1Seats} editor seats you can reassign as staff change', '{xk1X} xotskies an event'] as Unlock[],
    xk2: ['Lead-gen included, not an add-on', 'Live ops: staff arm the scanner on site', 'Activity log and stock CSV import', 'PDF export', 'Custom prize logic and game skins'] as Unlock[],
    enterprise: ['Unlimited events, projects and brands', '{entSeats} editor seats you can reassign as staff change', 'Bespoke game builds', 'SSO, an SLA and a named CSM', { soon: 'Atledi AI insights' }] as Unlock[],
  },
  calc: {
    presets: {
      single: { name: 'One market', sub: 'one brand' },
      regional: { name: 'Regional tour', sub: 'two brands' },
      national: { name: 'National tour', sub: 'leads and ops' },
      org: { name: 'Company-wide', sub: 'unlimited events' },
    },
    eventsAYear: 'events a year',
    inputs: {
      e: { label: 'Events a year', hint: 'One activation, deployed once. A two day festival is one event.' },
      x: { label: 'Xotskies per event', hint: 'Branded items handed out. Top-ups are {xotPrice} per {xotPack}.' },
      b: { label: 'Brands', hint: 'Each brand is its own inventory warehouse.' },
    },
    needs: {
      title: 'Anything else you need turned on?',
      leadGen: 'Leads',
      liveOps: 'Live ops',
      pdf: 'PDF export',
      sso: 'SSO',
      hintNone: 'Demographics and your branding are already on every plan, free included.',
      hintSome: '{list} set the lowest plan that qualifies.',
    },
    fineTune: 'Fine tune',
    detailRows: {
      t: { name: 'Xotski types', desc: 'Distinct items you track.' },
      l: { name: 'Languages', desc: 'Consumer-facing languages.' },
      pr: { name: 'Projects', desc: 'Saved, cloneable tour plans.' },
      ed: { name: 'Editor seats', desc: '{seat} a year each. Field staff and reporters are free.' },
    },
    summaryLine: 'Types {t}. Languages {l}. Projects {pr}. Editor seats {ed}.',
    summary: {
      types: 'types', language: 'language', languages: 'languages', project: 'project', projects: 'projects',
      seat: 'editor seat', seats: 'editor seats',
    },
    answer: {
      yourPlan: 'Your plan',
      perYear: 'per year',
      talkToUs: 'Talk to us',
      scoped: 'Scoped to your year.',
      freeInside: 'Free, for as long as you stay inside these numbers.',
      fromMonth: 'From {amount} a month, billed annually',
      monthEquiv: '{amount} a month equivalent, paid as each event goes live',
      month: '{amount} a month, billed annually',
      perEvent: 'or {amount} an event across {n} events',
      perEventFrom: 'or from {amount} an event across {n} events',
      startFree: 'Start free',
      startSeat: 'Start with an editor seat',
      choose: 'Choose {name}',
      turnsOn: 'This turns on',
      howBuilt: 'How this total is built',
      included: 'included',
      alcCompare: 'Same year bought à la carte',
      stepDown: 'Step down to {name}',
      stepUp: 'Step up to {name}',
      na: 'n/a',
      grace: '{pct}% grace overflow, so going a little past a cap will not stop your event.',
    },
    banners: {
      wall: 'Free stops at {freeEvents} events. Past that, pay per event à la carte from a {seat} editor seat, or move to XK1 for {xk1Events} events.',
      unlimited: 'Unlimited events added. XK2 plus unlimited events, {xk2Unlimited} a year. Cheaper than Enterprise unless you need SSO.',
      free: 'This fits the free plan. Build it, deploy it with your branding on, and pay when you outgrow {freeEvents} events.',
      enterprise: '{why} Enterprise stops metering events and starts metering seats.',
      enterpriseDefault: 'Unlimited events on a team license.',
      alcMore: 'Buying this year à la carte would cost {amount} more. The plan is the cheaper way.',
      xk2Close: 'XK2 is only {amount} more and adds leads, live ops and PDF export.',
    },
    neighbours: {
      free: '{freeEvents} events, {freeProjects} project, {freeGames}. Your branding and demographics still on.',
      xk1: 'All {games} games, the live dashboard, smart inventory, {xk1Events} events.',
      xk2: 'Leads, live ops, activity log, CSV import, PDF export.',
      enterprise: 'Unlimited events, {entSeats} seats, SSO and a CSM.',
    },
    why: {
      projectsFree: 'More than {n} project needs a plan.',
      projectsXk1: 'More than {n} projects needs XK2.',
      projectsXk2: 'More than {n} projects is Enterprise.',
      seatsEnt: 'More than {n} editor seats is Enterprise.',
      liveOps: 'Live ops unlocks at XK2.',
      pdf: 'PDF export unlocks at XK2.',
      sso: 'SSO is an Enterprise feature.',
    },
    lines: {
      'base.xk1': 'XK1 plan', 'base.xk2': 'XK2 plan', 'base.enterprise': 'Enterprise, starting at',
      'seat.editor': 'Editor seats',
      events: 'Event activations', eventsOver: 'Events beyond the plan', unlimitedAddon: 'Unlimited events add-on',
      brands: 'Brands', brandsOver: 'Brands beyond the plan',
      xotskies: 'Xotski top-ups', xotskiesOver: 'Xotski top-ups beyond the plan',
      types: 'Xotski types', typesOver: 'Types beyond the plan',
      languages: 'Languages', languagesOver: 'Languages beyond the plan',
      leadGen: 'Lead-gen activation', seatsExtra: 'Editor seats beyond the plan',
    },
  },
  assurances: [
    { title: 'Your branding on every plan', body: 'Your logo and colours on the game, Free included. Every plan carries the Powered by Onxite seal.' },
    { title: 'Demographics on every plan', body: 'Who actually engaged: age, gender, ethnicity and nationality, captured inclusively.' },
    { title: '{slots} game slots per station', body: 'Every plan, including Enterprise. Past {slots} a station stops being an experience.' },
    { title: 'Grace overflow', body: 'Run {paidGrace}% past a cap without being cut off mid-event. Free carries {freeGrace}%.' },
  ],
  detail: {
    title: 'All four plans, every line',
    intro: 'Open this if you want to audit the plans. You do not need it to buy.',
    showAll: 'Show all {n} lines',
    hide: 'Hide the full table',
    headFree: 'Free',
    headEnterprise: 'From {entPrice}',
    perYearShort: '/yr',
    soon: 'Coming soon',
    compare: [
      { label: 'Price, annual', desc: 'Billed once a year. Events are seasonal.', cells: ['{freePrice}', '{xk1Price}', '{xk2Price}', 'From {entPrice}'], kind: 'price' },
      { label: 'Events', desc: 'One activation deployed once. Free stops at {freeEvents}.', cells: [3, 15, 30, 'Unlimited'], kind: 'num' },
      { label: 'Projects', desc: 'Saved, cloneable tour plans.', cells: [1, 2, 4, 'Unlimited'], kind: 'num' },
      { label: 'Brands', desc: 'One inventory warehouse each.', cells: [1, 2, 4, 'Unlimited'], kind: 'num' },
      { label: 'Xotskies per event', desc: 'Before a {xotPrice} top-up per {xotPack}.', cells: [50, 200, 300, 'Custom'], kind: 'num' },
      { label: 'Xotski types', desc: 'Distinct items you track.', cells: [2, 2, 4, 'Unlimited'], kind: 'num' },
      { label: 'Games', desc: 'Spin Wheel, Scratch, Xinkit, Pirinola, Slots, Random draw, Soccer, Loteria.', cells: ['{freeGames}', 'All {games}', 'All {games}', 'All {games} plus bespoke'], kind: 'key' },
      { label: 'Your branding', desc: 'Your logo and colours on the game. Standard on every plan.', cells: [true, true, true, true], kind: 'text' },
      { label: 'Branding depth', desc: 'What moves up the plans.', cells: ['Logo and colours', 'Logo and colours', 'Custom prize logic and skins', 'Bespoke builds'], kind: 'key' },
      { label: 'Powered by Onxite seal', desc: 'On every plan and every game.', cells: [true, true, true, true], kind: 'text' },
      { label: 'Game slots per station', desc: 'Universal cap on every plan.', cells: ['{slots}', '{slots}', '{slots}', '{slots}'], kind: 'num' },
      { label: 'Languages', desc: 'Consumer-facing languages.', cells: [1, 1, 2, 'All'], kind: 'num' },
      { label: 'Editor seats included', desc: '{seat} a year for each extra. Reassign a seat when someone leaves.', cells: [1, 2, 3, 10], kind: 'num' },
      { label: 'Real-time dashboard', desc: 'Live plays, redemptions and stock as the event runs.', cells: [false, true, true, true], kind: 'key' },
      { label: 'Smart inventory', desc: 'Depletion forecast and reorder point per xotski type.', cells: [false, true, true, true], kind: 'key' },
      { label: 'Push notifications', desc: 'Low stock, station offline, prize threshold hit.', cells: [false, true, true, true], kind: 'key' },
      { label: 'User management', desc: 'Invite, scope and remove people on your account.', cells: [true, true, true, true], kind: 'text' },
      { label: 'Activity log', desc: 'Who changed what, when. Audit trail per event.', cells: [false, false, true, true], kind: 'key' },
      { label: 'Stock CSV import', desc: 'Bulk-load inventory instead of typing it in.', cells: [false, false, true, true], kind: 'key' },
      { label: 'Field staff (BAs)', desc: 'Brand ambassadors on site, scoped to the events assigned to them. Free.', cells: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'], kind: 'text' },
      { label: 'Reporters', desc: 'Email-gated report viewers who can comment. Free.', cells: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'], kind: 'text' },
      { label: 'Demographics', desc: 'Age, gender, ethnicity, nationality. On every plan.', cells: [true, true, true, true], kind: 'text' },
      { label: 'Lead-gen', desc: 'Contactable people, not just counts.', cells: [false, 'Add-on', 'Included', 'Included'], kind: 'key' },
      { label: 'Live ops', desc: 'Scanner arming and live inventory edits.', cells: [false, false, true, true], kind: 'key' },
      { label: 'PDF export', desc: 'Board-ready report export.', cells: [false, false, true, true], kind: 'key' },
      { label: 'Atledi AI insights', desc: 'Coming soon. AI insights on your events, metered by use.', cells: ['', '', '', ''], kind: 'text', soon: true },
      { label: 'Grace overflow', desc: 'How far past a cap you can run before anything stops.', cells: ['{freeGrace}%', '{paidGrace}%', '{paidGrace}%', '{paidGrace}%'], kind: 'num' },
      { label: 'Re-routing', desc: 'Send the player somewhere after the game.', cells: [false, true, true, true], kind: 'text' },
      { label: 'SSO, SLA, dedicated CSM', desc: 'The team-license layer.', cells: [false, false, false, true], kind: 'key' },
    ] as CompareRow[],
    alc: {
      title: 'À la carte, if you would rather build up',
      intro: 'From {alcFrom} events a year, XK1 costs less than à la carte. The calculator above already accounts for this.',
      note: 'À la carte starts once you are past Free: one editor seat at {seat} a year, then {event} an event with {alcX} xotskies included.',
      rows: [
        { item: 'Event activation', price: '{event}', unit: 'each', note: 'Real prizes, data on, your branding.' },
        { item: '+{xotPack} xotskies', price: '{xotPrice}', unit: 'per event', note: 'Starting allotment is {freeX} on Free, {xk1X} on paid plans.' },
        { item: '+1 xotski type', price: '{type}', unit: 'per project', note: '{freeTypes} types included on every plan.' },
        { item: '+1 language', price: '{language}', unit: 'per project', note: '{freeLangs} language included, {xk2Langs} at XK2.' },
        { item: '+1 brand or variant', price: '{brand}', unit: 'per year', note: 'Adds an inventory warehouse.' },
        { item: 'Activate lead-gen', price: '{leadGen}', unit: 'flat, paid plans', note: 'Included at XK2 and above.' },
        { item: 'Atledi AI insights', price: '', unit: '', note: 'Coming soon. AI insights on your events, metered by use.', soon: true },
        { item: 'Unlimited events', price: '{unlimited}', unit: 'per year, XK2 only', note: 'Lifts XK2 to unlimited events, {xk2Unlimited} total.' },
        { item: 'Bespoke game build', price: 'From {bespoke}', unit: 'per game', note: 'Built for your brand. Every paid plan already has all {games} games.' },
      ] as AlcRow[],
      seatRow: { item: '+1 editor seat', price: '{seat}', unit: 'per year', note: '{xk1Seats} included on XK1, {xk2Seats} on XK2, {entSeats} on Enterprise. Field staff and reporters are always free.' } as AlcRow,
      service: {
        title: 'Want us to run it?',
        body: 'Premium Activation is {premium} an event: white-glove setup, immersion QA and NFC where it applies. White Glove Program starts at {whiteGlove} a project and scales with scope.',
        atledi: 'Live Atledi insights during the event.',
        prices: [
          { name: 'Premium Activation', price: '{premium}', unit: 'an event' },
          { name: 'White Glove Program', price: 'From {whiteGlove}', unit: 'scales with scope' },
        ],
        cta: 'Talk to us',
      },
      demoNote: 'Demographics is not on this list because it is included on every plan, free included.',
    },
  },
  roles: {
    title: 'You only pay for the people who plan and run events',
    intro: 'The people who read the report and the people who work the booth are free and unlimited on every plan.',
    free: 'Free',
    cards: [
      { name: 'Field staff (BA)', price: 'Free', per: 'every plan', can: 'Works the events assigned to them. Arms the scanner at XK2.', count: 'Unlimited on every plan' },
      { name: 'Reporter', price: 'Free', per: 'every plan', can: 'Views the report through an email-gated invite and comments on it.', count: 'Unlimited on every plan' },
      { name: 'Editor', price: '{seat}', per: 'a year per seat', can: 'Plans the tour, runs events, manages inventory, and stages or pays activations.', count: '{xk1Seats} on XK1, {xk2Seats} on XK2, {entSeats} on Enterprise' },
    ],
  },
  enforcement: {
    title: 'Why building free is safe for you and for us',
    intro: 'Going live is a billing action, not a toggle. That is the whole enforcement model.',
    items: [
      { title: 'Preview is unlimited', body: 'On your own screen the preview never runs out. Iterate the wheel, the prize logic and the branding as long as you want.' },
      { title: 'A deployed test locks at {testPlays} plays', body: 'A QR out in the world carries a test banner, fake prizes and a {testHours} hour expiry. Enough to prove it works on real phones, not enough to serve a crowd.' },
      { title: 'Activation is the payment', body: 'The real version turns on when the activation clears. Every event mints its own QR, so a paid code cannot be reused next weekend.' },
    ],
  },
  faq: {
    title: 'Questions',
    items: [
      { q: 'Is it monthly?', a: 'No. Plans are annual because events are seasonal and bursty. Monthly figures are shown for comparison only.' },
      { q: 'What is a xotski?', a: 'A branded item you hand out at an event and want to track: a cup, a cap, a sample, a tote.' },
      { q: 'What counts as an event?', a: 'One activation, deployed once. A two day festival is one event.' },
      { q: 'Is my branding on the free plan?', a: 'Yes. Every plan, Free included, carries your logo and colours on the game. Every game also carries a small Powered by Onxite seal, on every plan.' },
      { q: 'Can a free account run more than {freeEvents} events?', a: 'No. Free stops at {freeEvents} events. For more, go à la carte from a {seat} editor seat plus {event} an event, or choose XK1 for {xk1Events} events.' },
      { q: 'How many games do I get?', a: 'Free gets {freeGames}. Every paid plan gets all {games}. What moves up is branding depth: custom prize logic and skins at XK2, bespoke builds at Enterprise.' },
      { q: 'Why only {slots} games per station?', a: 'Past about {slots} the consumer journey stops being an experience. The cap is universal, including Enterprise.' },
      { q: 'What do field staff and reporters cost?', a: 'Nothing. Field staff (BAs) and reporters are free and unlimited on every plan.' },
      { q: 'Can I add one more editor?', a: 'Yes. An extra editor seat is {seat} a year, prorated to your renewal date. {xk1Seats} are included on XK1, {xk2Seats} on XK2 and {entSeats} on Enterprise.' },
      { q: 'What is grace overflow?', a: 'Room to run past a cap mid-event instead of being cut off. Paid plans carry {paidGrace} percent, Free carries {freeGrace} percent.' },
      { q: 'Can I plan before I pay?', a: 'Yes. Build the whole tour, model it, test it on your own screen, and pay when you deploy.' },
      { q: 'What happens after {xk2Events} events?', a: 'Add unlimited events to XK2 for {unlimited} a year, which lands at {xk2Unlimited}. Or move to Enterprise if what you want is the team license.' },
      { q: 'Does Xotski measure?', a: 'No. Xotski reports and tracks: inventory, demographics, lead gen. Measurement is Xamplin.' },
    ],
  },
  footer: { partOf: 'Part of the ', ecosystem: ' ecosystem', powered: 'Powered by onxite™' },
  bar: { perYear: '{name}, per year' },
  chips: {
    events: '{n} events', brand: '{n} brand', brands: '{n} brands', xotskies: '{n} xotskies an event',
    project: '{n} project', projects: '{n} projects', seat: '{n} editor seat', seats: '{n} editor seats',
  },
  overlay: {
    back: '← Back',
    toApp: 'Go to the app',
    checkout: {
      subscribe: 'Subscribe to {name}, billed annually',
      perYear: 'per year',
      renews: 'Renews {date}',
      addonProrated: 'Add-ons bought mid-term are prorated to your account renewal on {date}',
      seatProrated: 'Extra seats mid-term are prorated to your renewal on {date}, then {amount} a year',
      subtotal: 'Subtotal',
      totalDue: 'Total due today',
      payWith: 'Pay with',
      stripeCta: 'Pay {amount} with Stripe',
      processing: 'Processing',
      stripeNote: 'You finish on Stripe’s secure page.',
      addonsNote: 'Add-ons and extra seats are billed as you use them, not today. Today you pay the plan.',
      signIn: 'Sign in to continue',
      unavailable: 'This plan is not ready for online checkout yet. Talk to us and we will set it up.',
    },
    free: {
      title: 'Start free',
      blurb: '{n} events a year with your branding on. Build the whole tour before you pay for anything.',
      placeholder: 'you@brand.com',
      cta: 'Create my account',
      note: 'No card. Unlimited previews on your own screen.',
      already: 'You already have Free. Every account starts there.',
    },
    contact: {
      enterpriseTitle: 'Enterprise',
      serviceTitle: 'Run it with us',
      enterpriseBlurb: 'Unlimited events, 10 seats, unlimited field staff and reporters, SSO and a dedicated CSM.',
      serviceBlurb: 'Premium Activation is one event we set up, QA and watch live. White Glove is a scoped program across a season.',
      enterprisePrice: 'Starting at $6,000',
      servicePrice: '$250 an event',
      enterpriseReasonDefault: 'Unlimited events, 10 seats and SSO on one team license.',
      serviceReason: 'White Glove Program starts at $500 a project and scales with scope.',
      fields: { name: 'Name', email: 'Work email', company: 'Company' },
      notePlaceholder: 'Markets, timing, brands',
      cta: 'Send my configuration',
      mailSubject: 'Xotski {kind} request from {company}',
      needFields: 'Your name, work email and company help us reply.',
    },
    success: {
      freeTitle: 'Your account is live',
      enterpriseTitle: 'We have your configuration',
      onTitle: 'You are on {name}',
      enterpriseBody: 'A CSM will reply within one business day with a scoped team license.',
      body: 'Everything you modelled carried over. Your account is ready.',
      stepsEnterprise: ['We scope the SOW against your {n} events', 'SSO and a named CSM at kickoff', 'Estimate on file: {estimate}'],
      steps: ['Your {n} {projectWord} and inventory ledger are set up', 'Invite field staff (BAs) and reporters for free', 'Preview as much as you like. Deploy when you are ready.'],
      custom: 'custom',
      back: 'Back to pricing',
    },
  },
  stamp: 'Model v18 / xotski.com pricing build, 29 Sep 2026',
};

export type PricingCopy = typeof PRICING_COPY_EN;

export const PRICING_COPY_ES: PricingCopy = {
  nav: { detail: 'Todo el detalle', startFree: 'Empieza gratis' },
  hero: {
    title1: 'Cuatro planes.',
    title2: 'Uno de ellos gratis',
    points: [
      { strong: 'Tecnología experiencial, primero.', rest: 'Hecha para el terreno, no para una hoja de cálculo.' },
      { strong: 'Regalos promocionales, en orden.', rest: 'Inventario por marca, por proyecto, por evento.' },
      { strong: 'Juegos que la gente juega de verdad.', rest: 'Inmersivos por diseño, con tu marca.' },
      { strong: 'IA sobre la curva de consumo.', rest: 'Lee el stock y te dice qué pedir después.' },
      { strong: 'Anual, no mensual.', rest: 'Los eventos son estacionales y a ráfagas. El precio debe ir igual.' },
    ],
  },
  modes: { plans: 'Los planes', calc: 'Calcula tu año' },
  plans: {
    names: { free: 'Gratis', alc: 'A la carta', xk1: 'XK1', xk2: 'XK2', enterprise: 'Enterprise' },
    cards: {
      free: { blurb: 'Arma toda la gira, corre {freeEvents} eventos, con tu marca.', cta: 'Empieza gratis' },
      xk1: { blurb: 'Los {games} juegos y un dashboard en vivo. {xk1Events} eventos al año.', cta: 'Elegir XK1' },
      xk2: { blurb: 'Leads, operación en vivo y reportes para una temporada de {xk2Events} eventos.', cta: 'Elegir XK2' },
      enterprise: { blurb: 'Deja de medir por evento y empieza a medir por asiento. SSO, SLA y un CSM asignado.', cta: 'Habla con nosotros' },
    },
    mostPopular: 'El más elegido',
    yourMatch: 'Tu plan',
    freePer: 'Gratis mientras te mantengas dentro del plan',
    perMonth: '{amount} al mes, facturado anualmente',
    perFrom: 'Desde {amount} al mes, facturado anualmente',
    from: 'Desde ',
    freeWall: 'Gratis llega hasta {freeEvents} eventos. Para más, pasa a XK1 o compra a la carta.',
    seal: 'Powered by Onxite en cada juego.',
    soon: 'Próximamente',
    notSure: '¿No sabes cuál? Calcula tu año y el precio sale solo.',
    openCalc: 'Abrir la calculadora',
  },
  unlocks: {
    free: ['{freeEvents} eventos al año, premios reales, datos activos', 'Tu logo y tus colores, con el sello Onxite', 'Demografía: quién participó de verdad', '{freeGames}', '{freeBrands} marca, {freeProjects} proyecto, {freeX} xotskies por evento'],
    alc: ['Un asiento de editor de {seat} al año, más {event} por evento', 'Tu marca en cada evento', 'Demografía en cada evento', 'Premios reales y control de inventario'],
    xk1: ['Los {games} juegos. Dashboard en tiempo real, inventario inteligente, alertas', '{xk1Events} eventos, {xk1Projects} proyectos, {xk1Brands} marcas', '{xk1Seats} asientos de editor que puedes reasignar', '{xk1X} xotskies por evento'],
    xk2: ['Lead-gen incluido, no como complemento', 'Operación en vivo: el staff activa el escáner en sitio', 'Bitácora de actividad e importación de stock por CSV', 'Exportación a PDF', 'Lógica de premios y skins a medida'],
    enterprise: ['Eventos, proyectos y marcas ilimitados', '{entSeats} asientos de editor que puedes reasignar', 'Juegos desarrollados a medida', 'SSO, SLA y un CSM asignado', { soon: 'Insights de IA de Atledi' }],
  },
  calc: {
    presets: {
      single: { name: 'Un mercado', sub: 'una marca' },
      regional: { name: 'Gira regional', sub: 'dos marcas' },
      national: { name: 'Gira nacional', sub: 'leads y operación' },
      org: { name: 'Toda la empresa', sub: 'eventos ilimitados' },
    },
    eventsAYear: 'eventos al año',
    inputs: {
      e: { label: 'Eventos al año', hint: 'Una activación, desplegada una vez. Un festival de dos días es un evento.' },
      x: { label: 'Xotskies por evento', hint: 'Artículos de marca entregados. Recargas de {xotPrice} por cada {xotPack}.' },
      b: { label: 'Marcas', hint: 'Cada marca es su propio almacén de inventario.' },
    },
    needs: {
      title: '¿Necesitas algo más activado?',
      leadGen: 'Leads',
      liveOps: 'Operación en vivo',
      pdf: 'Exportar PDF',
      sso: 'SSO',
      hintNone: 'La demografía y tu marca ya vienen en todos los planes, incluido Gratis.',
      hintSome: '{list} define el plan mínimo que aplica.',
    },
    fineTune: 'Ajuste fino',
    detailRows: {
      t: { name: 'Tipos de xotski', desc: 'Artículos distintos que controlas.' },
      l: { name: 'Idiomas', desc: 'Idiomas de cara al consumidor.' },
      pr: { name: 'Proyectos', desc: 'Planes de gira guardados y clonables.' },
      ed: { name: 'Asientos de editor', desc: '{seat} al año cada uno. Staff de campo y reporteros son gratis.' },
    },
    summaryLine: 'Tipos {t}. Idiomas {l}. Proyectos {pr}. Asientos de editor {ed}.',
    summary: {
      types: 'tipos', language: 'idioma', languages: 'idiomas', project: 'proyecto', projects: 'proyectos',
      seat: 'asiento de editor', seats: 'asientos de editor',
    },
    answer: {
      yourPlan: 'Tu plan',
      perYear: 'al año',
      talkToUs: 'Habla con nosotros',
      scoped: 'Cotizado a tu año.',
      freeInside: 'Gratis, mientras te mantengas dentro de estos números.',
      fromMonth: 'Desde {amount} al mes, facturado anualmente',
      monthEquiv: 'Equivale a {amount} al mes, pagado al activar cada evento',
      month: '{amount} al mes, facturado anualmente',
      perEvent: 'o {amount} por evento en {n} eventos',
      perEventFrom: 'o desde {amount} por evento en {n} eventos',
      startFree: 'Empieza gratis',
      startSeat: 'Empieza con un asiento de editor',
      choose: 'Elegir {name}',
      turnsOn: 'Esto se activa',
      howBuilt: 'Cómo se arma este total',
      included: 'incluido',
      alcCompare: 'El mismo año comprado a la carta',
      stepDown: 'Bajar a {name}',
      stepUp: 'Subir a {name}',
      na: 'n/d',
      grace: '{pct}% de margen de tolerancia: pasarte un poco de un límite no detiene tu evento.',
    },
    banners: {
      wall: 'Gratis llega hasta {freeEvents} eventos. Después, paga por evento a la carta desde un asiento de editor de {seat}, o pasa a XK1 con {xk1Events} eventos.',
      unlimited: 'Eventos ilimitados agregados. XK2 más eventos ilimitados, {xk2Unlimited} al año. Más barato que Enterprise, salvo que necesites SSO.',
      free: 'Esto cabe en el plan Gratis. Ármalo, despliégalo con tu marca y paga cuando superes {freeEvents} eventos.',
      enterprise: '{why} Enterprise deja de medir por evento y empieza a medir por asiento.',
      enterpriseDefault: 'Eventos ilimitados con una licencia de equipo.',
      alcMore: 'Comprar este año a la carta costaría {amount} más. El plan sale más barato.',
      xk2Close: 'XK2 cuesta solo {amount} más y suma leads, operación en vivo y exportación a PDF.',
    },
    neighbours: {
      free: '{freeEvents} eventos, {freeProjects} proyecto, {freeGames}. Tu marca y la demografía siguen activas.',
      xk1: 'Los {games} juegos, dashboard en vivo, inventario inteligente, {xk1Events} eventos.',
      xk2: 'Leads, operación en vivo, bitácora, importación CSV, exportación a PDF.',
      enterprise: 'Eventos ilimitados, {entSeats} asientos, SSO y un CSM.',
    },
    why: {
      projectsFree: 'Más de {n} proyecto requiere un plan.',
      projectsXk1: 'Más de {n} proyectos requiere XK2.',
      projectsXk2: 'Más de {n} proyectos es Enterprise.',
      seatsEnt: 'Más de {n} asientos de editor es Enterprise.',
      liveOps: 'La operación en vivo se activa en XK2.',
      pdf: 'La exportación a PDF se activa en XK2.',
      sso: 'SSO es una función de Enterprise.',
    },
    lines: {
      'base.xk1': 'Plan XK1', 'base.xk2': 'Plan XK2', 'base.enterprise': 'Enterprise, desde',
      'seat.editor': 'Asientos de editor',
      events: 'Activaciones de evento', eventsOver: 'Eventos por encima del plan', unlimitedAddon: 'Extra de eventos ilimitados',
      brands: 'Marcas', brandsOver: 'Marcas por encima del plan',
      xotskies: 'Recargas de xotskies', xotskiesOver: 'Recargas de xotskies por encima del plan',
      types: 'Tipos de xotski', typesOver: 'Tipos por encima del plan',
      languages: 'Idiomas', languagesOver: 'Idiomas por encima del plan',
      leadGen: 'Activación de lead-gen', seatsExtra: 'Asientos de editor por encima del plan',
    },
  },
  assurances: [
    { title: 'Tu marca en todos los planes', body: 'Tu logo y tus colores en el juego, incluido Gratis. Todos los planes llevan el sello Powered by Onxite.' },
    { title: 'Demografía en todos los planes', body: 'Quién participó de verdad: edad, género, etnicidad y nacionalidad, captados de forma incluyente.' },
    { title: '{slots} juegos por estación', body: 'En todos los planes, incluido Enterprise. Con más de {slots}, la estación deja de ser una experiencia.' },
    { title: 'Margen de tolerancia', body: 'Pásate {paidGrace}% de un límite sin que se corte tu evento. Gratis tiene {freeGrace}%.' },
  ],
  detail: {
    title: 'Los cuatro planes, línea por línea',
    intro: 'Ábrelo si quieres revisar los planes a detalle. No lo necesitas para comprar.',
    showAll: 'Ver las {n} líneas',
    hide: 'Ocultar la tabla',
    headFree: 'Gratis',
    headEnterprise: 'Desde {entPrice}',
    perYearShort: '/año',
    soon: 'Próximamente',
    compare: [
      { label: 'Precio anual', desc: 'Se factura una vez al año. Los eventos son de temporada.', cells: ['{freePrice}', '{xk1Price}', '{xk2Price}', 'Desde {entPrice}'], kind: 'price' },
      { label: 'Eventos', desc: 'Una activación desplegada una vez. Gratis llega hasta {freeEvents}.', cells: [3, 15, 30, 'Ilimitado'], kind: 'num' },
      { label: 'Proyectos', desc: 'Planes de gira guardados y clonables.', cells: [1, 2, 4, 'Ilimitado'], kind: 'num' },
      { label: 'Marcas', desc: 'Un almacén de inventario cada una.', cells: [1, 2, 4, 'Ilimitado'], kind: 'num' },
      { label: 'Xotskies por evento', desc: 'Antes de una recarga de {xotPrice} por cada {xotPack}.', cells: [50, 200, 300, 'A medida'], kind: 'num' },
      { label: 'Tipos de xotski', desc: 'Artículos distintos que controlas.', cells: [2, 2, 4, 'Ilimitado'], kind: 'num' },
      { label: 'Juegos', desc: 'Spin Wheel, Scratch, Xinkit, Pirinola, Slots, Random draw, Soccer, Lotería.', cells: ['{freeGames}', 'Los {games}', 'Los {games}', 'Los {games} y a medida'], kind: 'key' },
      { label: 'Tu marca', desc: 'Tu logo y tus colores en el juego. Incluido en todos los planes.', cells: [true, true, true, true], kind: 'text' },
      { label: 'Nivel de marca', desc: 'Lo que sube de plan en plan.', cells: ['Logo y colores', 'Logo y colores', 'Premios y skins a medida', 'Desarrollos a medida'], kind: 'key' },
      { label: 'Sello Powered by Onxite', desc: 'En todos los planes y en cada juego.', cells: [true, true, true, true], kind: 'text' },
      { label: 'Juegos por estación', desc: 'Límite universal en todos los planes.', cells: ['{slots}', '{slots}', '{slots}', '{slots}'], kind: 'num' },
      { label: 'Idiomas', desc: 'Idiomas de cara al consumidor.', cells: [1, 1, 2, 'Todos'], kind: 'num' },
      { label: 'Asientos de editor incluidos', desc: '{seat} al año por cada extra. Reasigna un asiento cuando alguien se va.', cells: [1, 2, 3, 10], kind: 'num' },
      { label: 'Dashboard en tiempo real', desc: 'Jugadas, canjes y stock en vivo durante el evento.', cells: [false, true, true, true], kind: 'key' },
      { label: 'Inventario inteligente', desc: 'Pronóstico de consumo y punto de reorden por tipo de xotski.', cells: [false, true, true, true], kind: 'key' },
      { label: 'Notificaciones', desc: 'Stock bajo, estación desconectada, umbral de premios alcanzado.', cells: [false, true, true, true], kind: 'key' },
      { label: 'Gestión de usuarios', desc: 'Invita, asigna y quita personas de tu cuenta.', cells: [true, true, true, true], kind: 'text' },
      { label: 'Bitácora de actividad', desc: 'Quién cambió qué y cuándo. Registro por evento.', cells: [false, false, true, true], kind: 'key' },
      { label: 'Importación de stock por CSV', desc: 'Carga el inventario en bloque en vez de capturarlo.', cells: [false, false, true, true], kind: 'key' },
      { label: 'Staff de campo (BAs)', desc: 'Brand ambassadors en sitio, limitados a los eventos que se les asignan. Gratis.', cells: ['Ilimitado', 'Ilimitado', 'Ilimitado', 'Ilimitado'], kind: 'text' },
      { label: 'Reporteros', desc: 'Ven el reporte con invitación por correo y pueden comentar. Gratis.', cells: ['Ilimitado', 'Ilimitado', 'Ilimitado', 'Ilimitado'], kind: 'text' },
      { label: 'Demografía', desc: 'Edad, género, etnicidad, nacionalidad. En todos los planes.', cells: [true, true, true, true], kind: 'text' },
      { label: 'Lead-gen', desc: 'Personas contactables, no solo conteos.', cells: [false, 'Complemento', 'Incluido', 'Incluido'], kind: 'key' },
      { label: 'Operación en vivo', desc: 'Activar el escáner y editar inventario en vivo.', cells: [false, false, true, true], kind: 'key' },
      { label: 'Exportación a PDF', desc: 'Reporte listo para presentar.', cells: [false, false, true, true], kind: 'key' },
      { label: 'Insights de IA de Atledi', desc: 'Próximamente. Insights de IA sobre tus eventos, cobrados por uso.', cells: ['', '', '', ''], kind: 'text', soon: true },
      { label: 'Margen de tolerancia', desc: 'Cuánto puedes pasarte de un límite antes de que algo se detenga.', cells: ['{freeGrace}%', '{paidGrace}%', '{paidGrace}%', '{paidGrace}%'], kind: 'num' },
      { label: 'Redirección', desc: 'Envía al jugador a otro destino después del juego.', cells: [false, true, true, true], kind: 'text' },
      { label: 'SSO, SLA, CSM dedicado', desc: 'La capa de licencia de equipo.', cells: [false, false, false, true], kind: 'key' },
    ] as CompareRow[],
    alc: {
      title: 'A la carta, si prefieres ir sumando',
      intro: 'Desde {alcFrom} eventos al año, XK1 sale más barato que a la carta. La calculadora de arriba ya lo toma en cuenta.',
      note: 'A la carta empieza cuando superas Gratis: un asiento de editor de {seat} al año, más {event} por evento con {alcX} xotskies incluidos.',
      rows: [
        { item: 'Activación de evento', price: '{event}', unit: 'cada una', note: 'Premios reales, datos activos, tu marca.' },
        { item: '+{xotPack} xotskies', price: '{xotPrice}', unit: 'por evento', note: 'Base de {freeX} en Gratis y {xk1X} en planes de pago.' },
        { item: '+1 tipo de xotski', price: '{type}', unit: 'por proyecto', note: '{freeTypes} tipos incluidos en todos los planes.' },
        { item: '+1 idioma', price: '{language}', unit: 'por proyecto', note: '{freeLangs} idioma incluido, {xk2Langs} en XK2.' },
        { item: '+1 marca o variante', price: '{brand}', unit: 'al año', note: 'Suma un almacén de inventario.' },
        { item: 'Activar lead-gen', price: '{leadGen}', unit: 'pago único, planes de pago', note: 'Incluido desde XK2.' },
        { item: 'Insights de IA de Atledi', price: '', unit: '', note: 'Próximamente. Insights de IA sobre tus eventos, cobrados por uso.', soon: true },
        { item: 'Eventos ilimitados', price: '{unlimited}', unit: 'al año, solo XK2', note: 'Lleva XK2 a eventos ilimitados, {xk2Unlimited} en total.' },
        { item: 'Juego a medida', price: 'Desde {bespoke}', unit: 'por juego', note: 'Hecho para tu marca. Todos los planes de pago ya tienen los {games} juegos.' },
      ] as AlcRow[],
      seatRow: { item: '+1 asiento de editor', price: '{seat}', unit: 'al año', note: '{xk1Seats} incluidos en XK1, {xk2Seats} en XK2, {entSeats} en Enterprise. Staff de campo y reporteros siempre son gratis.' } as AlcRow,
      service: {
        title: '¿Quieres que lo operemos nosotros?',
        body: 'Premium Activation cuesta {premium} por evento: montaje white-glove, QA de inmersión y NFC donde aplique. White Glove Program empieza en {whiteGlove} por proyecto y crece según el alcance.',
        atledi: 'Insights de Atledi en vivo durante el evento.',
        prices: [
          { name: 'Premium Activation', price: '{premium}', unit: 'por evento' },
          { name: 'White Glove Program', price: 'Desde {whiteGlove}', unit: 'crece según el alcance' },
        ],
        cta: 'Habla con nosotros',
      },
      demoNote: 'La demografía no aparece en esta lista porque viene incluida en todos los planes, incluido Gratis.',
    },
  },
  roles: {
    title: 'Solo pagas por quienes planean y operan eventos',
    intro: 'Quienes consultan el reporte y quienes atienden el stand son gratis e ilimitados en todos los planes.',
    free: 'Gratis',
    cards: [
      { name: 'Staff de campo (BA)', price: 'Gratis', per: 'todos los planes', can: 'Trabaja los eventos que se le asignan. Arma el escáner en XK2.', count: 'Ilimitados en todos los planes' },
      { name: 'Reportero', price: 'Gratis', per: 'todos los planes', can: 'Ve el reporte con una invitación por correo y lo comenta.', count: 'Ilimitados en todos los planes' },
      { name: 'Editor', price: '{seat}', per: 'al año por asiento', can: 'Planea la gira, opera eventos, gestiona inventario y prepara o paga activaciones.', count: '{xk1Seats} en XK1, {xk2Seats} en XK2, {entSeats} en Enterprise' },
    ],
  },
  enforcement: {
    title: 'Por qué construir gratis es seguro para ti y para nosotros',
    intro: 'Salir en vivo es un cobro, no un interruptor. Ese es todo el modelo de control.',
    items: [
      { title: 'La vista previa es ilimitada', body: 'En tu pantalla la vista previa nunca se acaba. Ajusta la ruleta, la lógica de premios y la marca las veces que quieras.' },
      { title: 'Una prueba desplegada se bloquea a las {testPlays} jugadas', body: 'Un QR publicado lleva un aviso de prueba, premios ficticios y vence en {testHours} horas. Basta para probar en teléfonos reales, no para atender a un público.' },
      { title: 'La activación es el pago', body: 'La versión real se enciende cuando se cobra la activación. Cada evento genera su propio QR, así que un código pagado no se reutiliza el fin de semana siguiente.' },
    ],
  },
  faq: {
    title: 'Preguntas',
    items: [
      { q: '¿Es mensual?', a: 'No. Los planes son anuales porque los eventos son de temporada. Las cifras mensuales solo son para comparar.' },
      { q: '¿Qué es un xotski?', a: 'Un artículo de marca que entregas en un evento y quieres controlar: un vaso, una gorra, una muestra, una bolsa.' },
      { q: '¿Qué cuenta como evento?', a: 'Una activación, desplegada una vez. Un festival de dos días es un evento.' },
      { q: '¿Mi marca aparece en el plan gratis?', a: 'Sí. Todos los planes, incluido Gratis, llevan tu logo y tus colores en el juego. Cada juego lleva también un pequeño sello Powered by Onxite, en todos los planes.' },
      { q: '¿Una cuenta gratis puede hacer más de {freeEvents} eventos?', a: 'No. Gratis llega hasta {freeEvents} eventos. Para más, compra a la carta desde un asiento de editor de {seat} más {event} por evento, o elige XK1 con {xk1Events} eventos.' },
      { q: '¿Cuántos juegos incluye?', a: 'Gratis incluye {freeGames}. Todos los planes de pago incluyen los {games}. Lo que sube es el nivel de marca: premios y skins a medida en XK2, desarrollos a medida en Enterprise.' },
      { q: '¿Por qué solo {slots} juegos por estación?', a: 'Con más de {slots}, el recorrido del consumidor deja de ser una experiencia. El límite es universal, incluido Enterprise.' },
      { q: '¿Cuánto cuestan el staff de campo y los reporteros?', a: 'Nada. El staff de campo (BAs) y los reporteros son gratis e ilimitados en todos los planes.' },
      { q: '¿Puedo sumar un editor más?', a: 'Sí. Un asiento de editor extra cuesta {seat} al año, prorrateado a tu fecha de renovación. XK1 incluye {xk1Seats}, XK2 {xk2Seats} y Enterprise {entSeats}.' },
      { q: '¿Qué es el margen de tolerancia?', a: 'Espacio para pasarte de un límite durante el evento sin que se corte. Los planes de pago tienen {paidGrace} por ciento y Gratis {freeGrace} por ciento.' },
      { q: '¿Puedo planear antes de pagar?', a: 'Sí. Arma toda la gira, modélala, pruébala en tu pantalla y paga cuando despliegues.' },
      { q: '¿Qué pasa después de {xk2Events} eventos?', a: 'Agrega eventos ilimitados a XK2 por {unlimited} al año, para un total de {xk2Unlimited}. O pasa a Enterprise si lo que buscas es la licencia de equipo.' },
      { q: '¿Xotski mide?', a: 'No. Xotski reporta y controla: inventario, demografía, lead gen. La medición es Xamplin.' },
    ],
  },
  footer: { partOf: 'Parte del ecosistema ', ecosystem: '', powered: 'Powered by onxite™' },
  bar: { perYear: '{name}, al año' },
  chips: {
    events: '{n} eventos', brand: '{n} marca', brands: '{n} marcas', xotskies: '{n} xotskies por evento',
    project: '{n} proyecto', projects: '{n} proyectos', seat: '{n} asiento de editor', seats: '{n} asientos de editor',
  },
  overlay: {
    back: '← Volver',
    toApp: 'Ir a la app',
    checkout: {
      subscribe: 'Suscribirse a {name}, facturado anualmente',
      perYear: 'al año',
      renews: 'Se renueva el {date}',

      addonProrated: 'Los extras comprados a mitad de periodo se prorratean hasta la renovación de tu cuenta el {date}',
      seatProrated: 'Los asientos extra a mitad de periodo se prorratean hasta tu renovación el {date}, después {amount} al año',
      subtotal: 'Subtotal',
      totalDue: 'Total a pagar hoy',
      payWith: 'Pagar con',
      stripeCta: 'Pagar {amount} con Stripe',
      processing: 'Procesando',
      stripeNote: 'Terminas en la página segura de Stripe.',
      addonsNote: 'Los extras y los asientos adicionales se facturan según los usas, no hoy. Hoy pagas el plan.',
      signIn: 'Inicia sesión para continuar',
      unavailable: 'Este plan aún no está listo para el pago en línea. Habla con nosotros y lo dejamos preparado.',
    },
    free: {
      title: 'Empieza gratis',
      blurb: '{n} eventos al año con tu marca. Monta la gira entera antes de pagar nada.',
      placeholder: 'tu@marca.com',
      cta: 'Crear mi cuenta',
      note: 'Sin tarjeta. Previas ilimitadas en tu propia pantalla.',
      already: 'Ya tienes el plan gratis. Toda cuenta empieza ahí.',
    },
    contact: {
      enterpriseTitle: 'Enterprise',
      serviceTitle: 'Lo llevamos contigo',
      enterpriseBlurb: 'Eventos ilimitados, 10 asientos, staff de campo y reporteros ilimitados, SSO y un CSM dedicado.',
      serviceBlurb: 'La Activación Premium es un evento que montamos, probamos y vigilamos en vivo. White Glove es un programa acotado para toda una temporada.',
      enterprisePrice: 'Desde $6,000',
      servicePrice: '$250 por evento',
      enterpriseReasonDefault: 'Eventos ilimitados, 10 asientos y SSO en una licencia de equipo.',
      serviceReason: 'El Programa White Glove empieza en $500 por proyecto y escala con el alcance.',
      fields: { name: 'Nombre', email: 'Email de trabajo', company: 'Empresa' },
      notePlaceholder: 'Mercados, fechas, marcas',
      cta: 'Enviar mi configuración',
      mailSubject: 'Solicitud {kind} de Xotski de {company}',
      needFields: 'Tu nombre, email de trabajo y empresa nos ayudan a responderte.',
    },
    success: {
      freeTitle: 'Tu cuenta está activa',
      enterpriseTitle: 'Tenemos tu configuración',
      onTitle: 'Estás en {name}',
      enterpriseBody: 'Un CSM te responderá en un día laborable con una licencia de equipo a medida.',
      body: 'Todo lo que modelaste se ha conservado. Tu cuenta está lista.',
      stepsEnterprise: ['Acotamos el SOW a tus {n} eventos', 'SSO y un CSM con nombre desde el arranque', 'Estimación registrada: {estimate}'],
      steps: ['Tus {n} {projectWord} y el libro de inventario están montados', 'Invita staff de campo (BAs) y reporteros gratis', 'Previsualiza todo lo que quieras. Despliega cuando estés listo.'],
      custom: 'a medida',
      back: 'Volver a precios',
    },
  },
  stamp: 'Model v18 / xotski.com pricing build, 29 Sep 2026',
};

/** `{name}` → valor, como hace `useT()`. */
export function fill(text: string, vars: Record<string, string | number>): string {
  let out = text;
  for (const k of Object.keys(vars)) out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(vars[k]));
  return out;
}
